import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { UnderbookedValue } from "./UnderbookedValue";
import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { ArrowRight } from "lucide-react";
import { DatePickerInput } from "@mantine/dates";
import dayjs from "dayjs";
import "dayjs/locale/ru";
import {
  fetchExceptionsReport,
  getReportXlsxUrl,
  type ExceptionKind,
  type ExceptionsReport,
  type ExceptionsReportQuery,
} from "../api/client";
import { useReportFetch } from "../hooks/useReportFetch";
import { REPORT_ABSENCE_TYPE_LABEL, employeeOptionsFromRows } from "./reportMeta";
import { getLastMondayBefore } from "./WeeklySummaryView";

dayjs.locale("ru");

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toIso(d: Date | null): string | null {
  if (!d) return null;
  return dayjs(d).format("YYYY-MM-DD");
}

const KIND_LABEL: Record<ExceptionKind, string> = {
  not_closed: "Не закрыт",
  underbooked: "Недосписался",
};

// Опции фильтра «Тип» (множественная формулировка — под KPI-карточки дашборда).
const KIND_FILTER_OPTIONS = [
  { value: "", label: "Все" },
  { value: "not_closed", label: "Не закрыли" },
  { value: "underbooked", label: "Недосписались" },
];

function isExceptionKind(v: string | null): v is ExceptionKind {
  return v === "not_closed" || v === "underbooked";
}

const KIND_COLOR: Record<ExceptionKind, string> = {
  not_closed: "gray",
  underbooked: "orange",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface ExceptionsReportViewProps {
  /**
   * Drill-down: вызывается при клике по строке (или иконке стрелки в конце
   * строки). Принимает id сотрудника и неделю (ISO YYYY-MM-DD). App переключает
   * таб на «Недельный свод» и предзаполняет фильтры.
   */
  onDrillDown?: (employeeId: number, weekStart: string) => void;
}

export function ExceptionsReportView({ onDrillDown }: ExceptionsReportViewProps = {}) {
  const defaultWeekStart = useMemo(() => getLastMondayBefore(), []);
  const [weekStart, setWeekStart] = useState<Date | null>(defaultWeekStart);
  const [employeeId, setEmployeeId] = useState<string | null>(null);
  // US-103.1: фильтр «Тип» (Не закрыли / Недосписались). Начальное значение —
  // из ?kind= (deep-link с KPI-карточки «Долг по часам» → underbooked).
  const [searchParams] = useSearchParams();
  const rawKind = searchParams.get("kind");
  const urlKind: ExceptionKind | null = isExceptionKind(rawKind) ? rawKind : null;
  const [manualKind, setManualKind] = useState<ExceptionKind | null | "auto">("auto");
  const kindFilter = manualKind === "auto" ? urlKind : manualKind;
  const setKindFilter = (v: ExceptionKind | null) => setManualKind(v);

  const query: ExceptionsReportQuery | null = useMemo(() => {
    const iso = toIso(weekStart);
    if (!iso) return null;
    return {
      weekStart: iso,
      ...(employeeId ? { employeeId: Number(employeeId) } : {}),
    };
  }, [weekStart, employeeId]);

  const { report, loading, error } = useReportFetch(query, fetchExceptionsReport);

  const employeeOptions = useMemo(
    () => (report ? employeeOptionsFromRows(report.rows) : []),
    [report],
  );

  // Клиентская фильтрация по типу исключения + пересчёт итогов под подвыборку.
  const displayReport = useMemo(() => {
    if (!report || !kindFilter) return report;
    const rows = report.rows.filter((r) => r.exception_kind === kindFilter);
    const sum = (
      key: "required_hours" | "allocated_hours" | "absence_hours_total" | "unallocated_hours",
    ) => rows.reduce((acc, r) => acc + Number(r[key]), 0).toFixed(2);
    return {
      ...report,
      rows,
      totals: {
        ...report.totals,
        required_hours: sum("required_hours"),
        allocated_hours: sum("allocated_hours"),
        absence_hours_total: sum("absence_hours_total"),
        unallocated_hours: sum("unallocated_hours"),
      },
    };
  }, [report, kindFilter]);

  return (
    <Stack gap="md">
      <Title order={3}>Отчёт по исключениям</Title>
      <Text size="xs" c="dimmed">
        Сотрудники, у которых выбранная неделя не закрыта, либо согласованный таймшит остался с
        долгом по часам. Максимум 100 сотрудников на страницу.
      </Text>
      <Group align="flex-end" gap="sm">
        <DatePickerInput
          label="Неделя (пн)"
          value={weekStart}
          onChange={(v) => {
            if (!v) {
              setWeekStart(null);
              return;
            }
            const d = v instanceof Date ? v : new Date(v);
            if (d.getDay() !== 1) return;
            setWeekStart(d);
          }}
          valueFormat="DD.MM.YYYY"
          excludeDate={(d) => {
            const date = d instanceof Date ? d : new Date(d);
            return date.getDay() !== 1;
          }}
          w={180}
        />
        <Select
          label="Сотрудник"
          placeholder="Все"
          clearable
          searchable
          data={employeeOptions}
          value={employeeId}
          onChange={setEmployeeId}
          w={260}
          nothingFoundMessage="Нет совпадений"
        />
        <Select
          label="Тип"
          data={KIND_FILTER_OPTIONS}
          value={kindFilter ?? ""}
          onChange={(v) => setKindFilter(isExceptionKind(v) ? v : null)}
          allowDeselect={false}
          w={170}
        />
        <Button
          component="a"
          href={
            query
              ? getReportXlsxUrl({
                  kind: "exceptions",
                  weekStart: query.weekStart,
                  employeeId: query.employeeId,
                })
              : undefined
          }
          disabled={loading || !query}
          variant="default"
          aria-label="Экспорт в Excel"
        >
          Экспорт в Excel
        </Button>
      </Group>

      {loading && <Loader size="sm" />}
      {error && (
        <Alert color="red" title="Не удалось загрузить отчёт">
          {error}
        </Alert>
      )}
      {!loading && !error && displayReport && (
        displayReport.rows.length === 0 && kindFilter ? (
          <Alert color="green" variant="light">
            Нет сотрудников типа «{KIND_LABEL[kindFilter]}» за выбранную неделю.
          </Alert>
        ) : (
          <ExceptionsReportTable report={displayReport} onDrillDown={onDrillDown} />
        )
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------

interface ExceptionsReportTableProps {
  report: ExceptionsReport;
  onDrillDown?: (employeeId: number, weekStart: string) => void;
}

function ExceptionsReportTable({ report, onDrillDown }: ExceptionsReportTableProps) {
  if (report.rows.length === 0) {
    return (
      <Alert color="green" variant="light">
        Все сотрудники закрыли период за выбранную неделю.
      </Alert>
    );
  }

  const { rows, totals } = report;

  return (
    <ScrollArea>
      <Table withTableBorder highlightOnHover striped>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Сотрудник</Table.Th>
            <Table.Th>Статус</Table.Th>
            <Table.Th>Тип</Table.Th>
            <Table.Th ta="right">Норма</Table.Th>
            <Table.Th ta="right">Распределено</Table.Th>
            <Table.Th ta="right">Отсутствие</Table.Th>
            <Table.Th ta="right">Нераспределено</Table.Th>
            {onDrillDown && <Table.Th />}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row) => {
            const handleDrill = () => {
              onDrillDown?.(row.employee.id, report.week_start);
            };
            return (
              <Table.Tr
                key={row.employee.id}
                data-testid={`exception-row-${row.employee.id}`}
                style={onDrillDown ? { cursor: "pointer" } : undefined}
                onClick={onDrillDown ? handleDrill : undefined}
              >
                <Table.Td>{row.employee.full_name}</Table.Td>
                <Table.Td>
                  <Text size="sm" span>
                    {row.timesheet_status_display}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Badge color={KIND_COLOR[row.exception_kind]} variant="light">
                    {KIND_LABEL[row.exception_kind]}
                  </Badge>
                </Table.Td>
                <Table.Td ta="right">{row.required_hours}</Table.Td>
                <Table.Td ta="right">{row.allocated_hours}</Table.Td>
                <Table.Td ta="right">{row.absence_hours_total}</Table.Td>
                <Table.Td ta="right">
                  <UnderbookedValue
                    value={row.unallocated_hours}
                    danger={Number(row.unallocated_hours) > 0}
                  />
                </Table.Td>
                {onDrillDown && (
                  <Table.Td ta="right" style={{ width: 36 }}>
                    <Tooltip label="Перейти в Недельный свод" withArrow>
                      <ArrowRight
                        size={14}
                        aria-label="Перейти в свод"
                        style={{ verticalAlign: "middle", opacity: 0.6 }}
                      />
                    </Tooltip>
                  </Table.Td>
                )}
              </Table.Tr>
            );
          })}
          {/* Итого */}
          <Table.Tr>
            <Table.Td fw={700}>Итого</Table.Td>
            <Table.Td></Table.Td>
            <Table.Td></Table.Td>
            <Table.Td ta="right" fw={700}>
              {totals.required_hours}
            </Table.Td>
            <Table.Td ta="right" fw={700}>
              {totals.allocated_hours}
            </Table.Td>
            <Table.Td ta="right" fw={700}>
              {totals.absence_hours_total}
            </Table.Td>
            <Table.Td ta="right" fw={700}>
              <UnderbookedValue
                value={totals.unallocated_hours}
                danger={Number(totals.unallocated_hours) > 0}
                bold
              />
            </Table.Td>
            {onDrillDown && <Table.Td />}
          </Table.Tr>
        </Table.Tbody>
      </Table>
      {report.absence_types.length > 0 && (
        <Text size="xs" c="dimmed" mt="xs">
          Типы отсутствий в выбранной неделе:{" "}
          {report.absence_types.map((t) => REPORT_ABSENCE_TYPE_LABEL[t] ?? t).join(", ")}.
        </Text>
      )}
    </ScrollArea>
  );
}
